import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api, ApiError, FUEL_UNITS, type FuelUnit, type OrgProfile } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Alert, Button, Field, Input, PageTitle, Panel, Select } from "../components/ui";

const UNIT_LABEL: Record<FuelUnit, string> = {
  litres: "Litres (L)",
  gallons: "Gallons (gal)",
  pounds: "Pounds (lbs)",
  kilograms: "Kilograms (kg)",
};

/** The empty option: each mission shown in whatever unit it was logged in. */
const AS_LOGGED = "";


const LOGO_LIMIT_BYTES = 1_048_576;

/**
 * Who the company is on its exported sheets: the name, a business number and a
 * logo. Edits are held until Save, because a half-typed name should never
 * reach a document that goes to a client; the logo is its own action.
 */
function Company({ isAdmin }: { isAdmin: boolean }) {
  const auth = useAuth();
  const [profile, setProfile] = useState<OrgProfile | null>(null);
  const [name, setName] = useState("");
  const [abn, setAbn] = useState("");
  const [busy, setBusy] = useState<"save" | "logo" | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | undefined>();
  const file = useRef<HTMLInputElement>(null);

  const adopt = (p: OrgProfile) => {
    setProfile(p);
    setName(p.name);
    setAbn(p.abn ?? "");
  };

  useEffect(() => {
    let cancelled = false;
    api.getOrgProfile().then(
      (p) => !cancelled && adopt(p),
      (err) => !cancelled && setError(err instanceof ApiError ? err.message : "Couldn't load the company details."),
    );
    return () => { cancelled = true; };
  }, []);

  if (auth.status !== "authenticated") return null;
  const me = auth.me;

  const dirty = profile !== null && (name.trim() !== profile.name || abn.trim() !== (profile.abn ?? ""));

  async function save() {
    setBusy("save");
    setError(null);
    setNameError(undefined);
    setSaved(false);
    try {
      const next = await api.updateOrgSettings({ name, abn: abn.trim() === "" ? null : abn });
      adopt(next);
      // The header and the rest of the app read the name from here.
      auth.setMe({ ...me, organization: { ...me.organization, name: next.name } });
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setNameError(err.fieldError("name"));
        if (!err.fieldError("name")) setError(err.message);
      } else {
        setError("Couldn't save the company details.");
      }
    } finally {
      setBusy(null);
    }
  }

  async function chooseLogo(chosen: File | undefined) {
    if (!chosen) return;
    setError(null);
    // Checked here for a quick answer; the server checks again, because a
    // browser check is a courtesy, not a control.
    if (chosen.size > LOGO_LIMIT_BYTES) {
      setError(`That file is ${(chosen.size / 1_048_576).toFixed(1)} MB. The limit is 1 MB.`);
      return;
    }
    setBusy("logo");
    try {
      await api.uploadLogo(chosen);
      adopt(await api.getOrgProfile());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't upload that image.");
    } finally {
      setBusy(null);
      if (file.current) file.current.value = "";
    }
  }

  async function removeLogo() {
    setBusy("logo");
    setError(null);
    try {
      await api.removeLogo();
      adopt(await api.getOrgProfile());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't remove the logo.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <p className="section-label mb-2.5 px-1">Company</p>
      {error && <div className="mb-3"><Alert>{error}</Alert></div>}
      <Panel className="px-5 py-5 flex flex-col gap-5">
        <Field label="Company name" error={nameError}>
          {(id) => (
            <Input id={id} value={name} disabled={!isAdmin || profile === null} invalid={!!nameError}
              onChange={(e) => { setName(e.target.value); setSaved(false); }} />
          )}
        </Field>
        <Field label="ABN or business number" hint="Printed in the footer of exported sheets. Optional.">
          {(id) => (
            <Input id={id} value={abn} disabled={!isAdmin || profile === null} maxLength={32}
              onChange={(e) => { setAbn(e.target.value); setSaved(false); }} />
          )}
        </Field>

        <div>
          <p className="text-[0.9rem] font-semibold text-ink mb-1.5">Logo</p>
          <div className="flex items-center gap-4 flex-wrap">
            {/* On white whatever the theme: it prints on white paper. */}
            <div className="w-48 h-20 rounded-lg bg-white border border-rule grid place-items-center overflow-hidden">
              {profile?.hasLogo ? (
                <img src={`/v1/org/logo?v=${encodeURIComponent(profile.logoUpdatedAt ?? "")}`} alt="Company logo"
                  className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-[0.85rem] text-neutral-500">No logo</span>
              )}
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <input ref={file} type="file" accept="image/png,image/jpeg" className="sr-only" aria-label="Choose a logo image"
                  onChange={(e) => void chooseLogo(e.target.files?.[0])} />
                <Button variant="secondary" busy={busy === "logo"} onClick={() => file.current?.click()}>
                  {profile?.hasLogo ? "Replace" : "Upload"}
                </Button>
                {profile?.hasLogo && (
                  <Button variant="destructive" disabled={busy !== null} onClick={() => void removeLogo()}>Remove</Button>
                )}
              </div>
            )}
          </div>
          <p className="mt-2 text-[0.85rem] text-ink-faint">
            PNG or JPEG, up to 1 MB. Drawn top right of the first page of each exported sheet.
          </p>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-3">
            <Button busy={busy === "save"} disabled={!dirty || busy !== null} onClick={() => void save()}>Save</Button>
            {saved && !dirty && <span className="text-[0.9rem] text-good">Saved</span>}
          </div>
        )}
      </Panel>
      {!isAdmin && <p className="mt-2.5 px-1 text-[0.9rem] text-ink-soft">Only an admin can change these.</p>}
    </div>
  );
}

export function Settings() {
  const auth = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (auth.status !== "authenticated") return null;
  const { me } = auth;
  const isAdmin = me.user.role === "admin";
  const current = me.organization.reportFuelUnit ?? AS_LOGGED;

  // Saves on selection, as an iOS settings row does — there is one choice
  // here, and a separate Save button would only be something to forget.
  async function change(value: string) {
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const reportFuelUnit = value === AS_LOGGED ? null : (value as FuelUnit);
      await api.updateOrgSettings({ reportFuelUnit });
      auth.setMe({ ...me, organization: { ...me.organization, reportFuelUnit } });
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that setting.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-8 max-w-2xl">
      <PageTitle>Settings</PageTitle>

      {error && <Alert>{error}</Alert>}

      <Company isAdmin={isAdmin} />

      <div>
        <p className="section-label mb-2.5 px-1">ODR</p>
        <Panel>
          <Link
            to="/app/settings/odr"
            className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-panel-raised transition-colors rounded-xl"
          >
            <div>
              <p className="text-[1rem] font-semibold text-ink">What your iPads log and how hours are reported</p>
              <p className="mt-0.5 text-[0.9rem] text-ink-soft">Fuel prompts, the cooldown timer, and rounding of engine and flight time.</p>
            </div>
            <ChevronRight size={20} className="text-ink-faint shrink-0" aria-hidden />
          </Link>
        </Panel>
      </div>

      <div>
        <p className="section-label mb-2.5 px-1">Reports</p>
        <Panel className="px-5 py-4">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <label htmlFor="fuel-unit" className="text-[1rem] font-semibold text-ink">
              Fuel unit
            </label>
            <div className="flex items-center gap-3">
              {saving && <span className="text-[0.9rem] text-ink-soft">Saving…</span>}
              {saved && !saving && <span className="text-[0.9rem] text-good">Saved</span>}
              <Select
                id="fuel-unit"
                value={current}
                disabled={!isAdmin || saving}
                onChange={(e) => void change(e.target.value)}
                className="min-w-56"
              >
                <option value={AS_LOGGED}>As each mission was logged</option>
                {FUEL_UNITS.map((u) => (
                  <option key={u} value={u}>{UNIT_LABEL[u]}</option>
                ))}
              </Select>
            </div>
          </div>
        </Panel>
        <p className="mt-2.5 px-1 text-[0.9rem] text-ink-soft leading-relaxed">
          Used for fuel figures on this dashboard and in exported PDFs. Pilots still see
          each aircraft's own unit in the app.
          {!isAdmin && " Only an admin can change this."}
        </p>
      </div>
    </div>
  );
}
